import * as React from 'react'

import {
  Drawer,
  DrawerContent,
  DrawerTitle,
} from '~/client/src/shared/components/shadcn-ui/drawer'
import { SidePanelOverlayProvider } from '~/client/src/shared/components/SidePanel/SidePanelOverlayContext'
import Localization from '~/client/src/shared/localization/LocalizationManager'

interface IProps {
  open: boolean
  onClose: () => void
  onCloseComplete?: () => void
  children: React.ReactNode
}

// Below this many pixels of viewport shrink, treat the on-screen keyboard as
// down. Matches vaul's own 60px "did the keyboard really change" threshold.
const KEYBOARD_MIN_INSET = 60

const MobileResourceEditDrawer: React.FC<IProps> = ({
  open,
  onClose,
  onCloseComplete,
  children,
}) => {
  const contentRef = React.useRef<HTMLDivElement>(null)

  // vaul's repositionInputs lifts the drawer above the keyboard by writing inline
  // style.bottom/style.height onto the DrawerContent, but its reset is gated behind
  // `isInput(activeElement) || keyboardIsOpen.current` (vaul/dist/index.mjs). A
  // blur-then-hide sequence (tap Done, tap outside, or TimeCombobox's blur-to-open
  // -the-list) reaches that reset with focus already on <body> and the parity flag
  // cleared, so vaul skips the reset and strands the lifted geometry. Because the
  // form footer AND the portaled HoO picker sheet both live inside this one
  // DrawerContent, the stale styles show as dead space under Save (iOS), preserved
  // keyboard space (Android), and the mispositioned/blank time sheet.
  //
  // Fix: whenever the keyboard is down, drop vaul's inline bottom/height so the
  // drawer falls back to its CSS geometry. We only ever CLEAR (never add a lift),
  // so we never fight vaul while the keyboard is genuinely open, never double-lift
  // on top of an Android system pan, and behave identically under adjustResize and
  // adjustPan. repositionInputs stays on, so vaul's scroll-lock and open-time lift
  // are untouched.
  React.useEffect(() => {
    const el = contentRef.current
    const vv = window.visualViewport
    if (!open || !el || !vv) return

    const clearStaleGeometry = () => {
      if (el.style.bottom) el.style.bottom = ''
      if (el.style.height) el.style.height = ''
    }

    const onViewportChange = () => {
      if (window.innerHeight - vv.height <= KEYBOARD_MIN_INSET) {
        // Run after vaul's own synchronous resize handler for this event so our
        // clear wins over any px-height reset it just wrote.
        requestAnimationFrame(clearStaleGeometry)
      }
    }

    // Backstop for the keyboard-hide case where the final viewport resize reports
    // an intermediate inset and no further event fires (seen on Android): after a
    // blur, re-check once the keyboard has settled and clear if it is down.
    let settleTimer: ReturnType<typeof setTimeout> | undefined
    const onFocusOut = () => {
      clearTimeout(settleTimer)
      settleTimer = setTimeout(onViewportChange, 400)
    }

    // Native scroll-into-view fires at focus time, before vaul repositions the
    // drawer above the keyboard, so a lower field can stay hidden. Re-run it once
    // the keyboard has settled.
    let revealTimer: ReturnType<typeof setTimeout> | undefined
    const revealFocusedInput = () => {
      const active = document.activeElement
      if (
        active instanceof HTMLElement &&
        (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')
      ) {
        active.scrollIntoView({ block: 'nearest' })
      }
    }
    const onFocusIn = (event: FocusEvent) => {
      const target = event.target
      if (
        target instanceof HTMLElement &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')
      ) {
        clearTimeout(revealTimer)
        revealTimer = setTimeout(revealFocusedInput, 350)
      }
    }

    vv.addEventListener('resize', onViewportChange)
    vv.addEventListener('scroll', onViewportChange)
    el.addEventListener('focusin', onFocusIn)
    el.addEventListener('focusout', onFocusOut)

    return () => {
      vv.removeEventListener('resize', onViewportChange)
      vv.removeEventListener('scroll', onViewportChange)
      el.removeEventListener('focusin', onFocusIn)
      el.removeEventListener('focusout', onFocusOut)
      clearTimeout(revealTimer)
      clearTimeout(settleTimer)
      clearStaleGeometry()
    }
  }, [open])

  return (
    <Drawer
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) {
          onClose()
        }
      }}
    >
      <DrawerContent
        ref={contentRef}
        className="tw:h-[85vh] tw:max-h-[85vh] tw:min-h-0 tw:outline-none"
        aria-describedby={undefined}
        onPointerDownOutside={event => {
          // The user-detail card is portaled to `body` and lives outside the
          // drawer's React tree, so Radix sees clicks on it as "outside" and
          // would dismiss the drawer. Treat clicks inside the user card layer
          // as inside.
          const target = event.target as Element | null
          if (target?.closest('[data-user-card-portal]')) {
            event.preventDefault()
          }
        }}
        onAnimationEnd={event => {
          if (
            !open &&
            event.target === event.currentTarget &&
            onCloseComplete
          ) {
            onCloseComplete()
          }
        }}
      >
        <DrawerTitle className="tw:sr-only">
          {Localization.translator.edit_verb}
        </DrawerTitle>
        <div className="tw:relative tw:flex tw:flex-1 tw:min-h-0 tw:flex-col">
          <SidePanelOverlayProvider>{children}</SidePanelOverlayProvider>
        </div>
      </DrawerContent>
    </Drawer>
  )
}

export default MobileResourceEditDrawer
